import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, NumberInput, NumberInputField, Select, Box, Checkbox, SimpleGrid } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { GdtSymbolSvg } from '../../tools/cad/GdtSymbolGlyph';
import {
    GDT_SYMBOLS, GDT_MATERIALS, buildToleranceRows,
    type GdtSymbol, type GdtMaterial, type ToleranceData,
} from '../../core/commands/toleranceModel';
import { runInteraction, PT } from '../../core/commands/runners/lib';
import { undoableAdd, makeEntity } from '../../core/commands/entityOps';

/**
 * Geometric Tolerance dialog (Faz 8.3, OCS tolerance_dialog parity): the
 * fixed compartment layout — 1 symbol + 2 tolerance entries (value, diameter
 * flag, material modifier) + 3 datum compartments + projected zone — then a
 * click places the structural 'tolerance' entity at the picked point.
 */
export function ToleranceDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'tolerance');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const activeLayerId = useAppStore((s) => s.activeLayerId);

    const [symbol, setSymbol] = useState<GdtSymbol | null>('j');
    const [tolerance1, setTolerance1] = useState('0.05');
    const [diameter1, setDiameter1] = useState(false);
    const [material1, setMaterial1] = useState<GdtMaterial | null>(null);
    const [tolerance2, setTolerance2] = useState('');
    const [diameter2, setDiameter2] = useState(false);
    const [material2, setMaterial2] = useState<GdtMaterial | null>(null);
    const [datum1, setDatum1] = useState('');
    const [datum2, setDatum2] = useState('');
    const [datum3, setDatum3] = useState('');
    const [projectedZone, setProjectedZone] = useState(false);
    const [projectedHeight, setProjectedHeight] = useState('');
    const [datumIdentifier, setDatumIdentifier] = useState('');
    const [textHeight, setTextHeight] = useState(0.3);

    const spec = {
        symbol, tolerance1, diameter1, material1,
        tolerance2, diameter2, material2,
        datum1, datum2, datum3,
        projectedZone, projectedHeight, datumIdentifier,
    };
    const rows = buildToleranceRows(spec);
    const canPlace = rows.length > 0;

    const place = () => {
        if (!canPlace) return;
        onClose();
        const data: ToleranceData = { rows, textHeight, position: [0, 0, 0] };
        runInteraction({
            steps: [PT('TOLERANCE: frame insertion point (lower-left)')],
            complete: (pts) => {
                const p = pts[0];
                undoableAdd([makeEntity('tolerance', `Tolerance-${Date.now()}`, {
                    ...data, position: [p.x, p.y, p.z],
                }, activeLayerId)]);
                return 'Feature control frame placed (structural GD&T model).';
            },
        });
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.tolerance.title')}
            size="lg"
            footer={
                <HStack justify="flex-end" w="full" spacing={2}>
                    <DialogButton variant="primary" onClick={place} isDisabled={!canPlace}>
                        {t('ui.cad.tolerance.place')}
                    </DialogButton>
                    <DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>
                </HStack>
            }
        >
            <VStack spacing={4} align="stretch">
                {/* Symbol grid (15 GD&T characteristics, OCS fixed single-symbol
                    compartment — add/remove compartments stays out, extra rows
                    come through the datum identifier only). */}
                <Box>
                    <Text fontSize="sm" mb={1} color="var(--color-text-secondary)">{t('ui.cad.tolerance.symbol')}</Text>
                    <SimpleGrid columns={8} spacing={1}>
                        {GDT_SYMBOLS.map((s) => (
                            <Box
                                key={s.code}
                                as="button"
                                p={1}
                                borderRadius="sm"
                                border="1px solid"
                                borderColor={symbol === s.code ? 'brand.500' : 'border.base'}
                                bg={symbol === s.code ? 'bg.active' : 'transparent'}
                                color={symbol === s.code ? 'brand.500' : 'text.primary'}
                                title={s.label}
                                onClick={() => setSymbol(symbol === s.code ? null : s.code)}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                            >
                                <GdtSymbolSvg symbol={s.code} size={22} />
                            </Box>
                        ))}
                    </SimpleGrid>
                </Box>

                {/* Tolerance compartments ×2 (value + diameter + material). */}
                {([1, 2] as const).map((n) => {
                    const value = n === 1 ? tolerance1 : tolerance2;
                    const setValue = n === 1 ? setTolerance1 : setTolerance2;
                    const diameter = n === 1 ? diameter1 : diameter2;
                    const setDiameter = n === 1 ? setDiameter1 : setDiameter2;
                    const material = n === 1 ? material1 : material2;
                    const setMaterial = n === 1 ? setMaterial1 : setMaterial2;
                    return (
                        <HStack key={n} justify="space-between" spacing={3}>
                            <Text fontSize="sm" w="110px" flexShrink={0} color="var(--color-text-secondary)">
                                {t(`ui.cad.tolerance.tolerance${n}`)}
                            </Text>
                            <Checkbox size="sm" isChecked={diameter} onChange={(e) => setDiameter(e.target.checked)}>
                                <Text fontSize="sm">⌀ {t('ui.cad.tolerance.diameter')}</Text>
                            </Checkbox>
                            <Input
                                size="sm" w="140px" value={value}
                                bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                                onChange={(e) => setValue(e.target.value)}
                            />
                            <Select
                                size="sm" w="110px" flexShrink={0}
                                value={material ?? ''}
                                onChange={(e) => setMaterial((e.target.value || null) as GdtMaterial | null)}
                            >
                                <option value="">{t('ui.cad.tolerance.none')}</option>
                                {GDT_MATERIALS.map((m) => (
                                    <option key={m.code} value={m.code}>{m.label}</option>
                                ))}
                            </Select>
                        </HStack>
                    );
                })}

                {/* Datum compartments ×3 (OCS fixed layout). */}
                {([1, 2, 3] as const).map((n) => (
                    <HStack key={n} justify="space-between" spacing={3}>
                        <Text fontSize="sm" w="110px" flexShrink={0} color="var(--color-text-secondary)">
                            {t('ui.cad.tolerance.datum', { n })}
                        </Text>
                        <Input
                            size="sm" w="140px"
                            value={n === 1 ? datum1 : n === 2 ? datum2 : datum3}
                            bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                            onChange={(e) => (n === 1 ? setDatum1 : n === 2 ? setDatum2 : setDatum3)(e.target.value)}
                        />
                    </HStack>
                ))}

                <HStack spacing={6}>
                    <Checkbox size="sm" isChecked={projectedZone} onChange={(e) => setProjectedZone(e.target.checked)}>
                        <Text fontSize="sm">{t('ui.cad.tolerance.projectedZone')}</Text>
                    </Checkbox>
                    {projectedZone && (
                        <Input
                            size="sm" w="110px" value={projectedHeight}
                            placeholder={t('ui.cad.tolerance.projectedHeight')}
                            bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                            onChange={(e) => setProjectedHeight(e.target.value)}
                        />
                    )}
                </HStack>

                <HStack justify="space-between" spacing={3}>
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.tolerance.height')}</Text>
                    <NumberInput size="sm" w="110px" value={textHeight} min={0.05} step={0.05}
                        onChange={(_, v) => setTextHeight(Number.isFinite(v) && v > 0 ? v : textHeight)}>
                        <NumberInputField bg="var(--color-bg-primary)" borderColor="var(--color-border)" />
                    </NumberInput>
                </HStack>
                <HStack justify="space-between" spacing={3}>
                    <Text fontSize="sm" color="var(--color-text-secondary)">{t('ui.cad.tolerance.datumIdentifier')}</Text>
                    <Input
                        size="sm" w="140px" value={datumIdentifier}
                        bg="var(--color-bg-primary)" borderColor="var(--color-border)"
                        onChange={(e) => setDatumIdentifier(e.target.value)}
                    />
                </HStack>
                <Text fontSize="xs" color="var(--color-text-secondary)">
                    {canPlace ? '' : t('ui.cad.tolerance.emptyHint')}
                </Text>
            </VStack>
        </AppDialog>
    );
}
